/**
 * Nathalie Robert — monthly evolution advice.
 *
 * Fires on the 28th of every month IF Nathalie has been hired AND at
 * least one actionable insight is found in the live KPIs.
 *
 * Each insight is grounded in real game state — no fictional events.
 * Categories considered (in priority order):
 *
 *   1. Fleet occupancy high & stable → suggest more vehicles
 *   2. Drivers below 50 satisfaction → suggest a retention action
 *   3. Specialty fleet under-used (frigo / ADR / animaux with no
 *      contract in the matching category this month)
 *   4. Cash buildup with no investment (treasury > runway × 6 months)
 *
 * Returns the Messagerie payload OR null when no insight applies
 * (Nathalie doesn't talk for the sake of talking).
 *
 * 31/05/2026 — Nathalie active assistant pattern v2 (recadré).
 */

const TTL_DAYS = 30;

const fmt = (n) => Math.round(n || 0).toLocaleString('fr-FR');

/**
 * @param {object} state  live store state.
 * @returns {object|null} Messagerie payload OR null.
 */
export function buildNathalieMonthlyAdvice(state) {
  if (!state || typeof state !== 'object') return null;
  const hired = !!(state.hiredSupport && state.hiredSupport['role-nathalie-robert']);
  if (!hired) return null;

  const insights = [];
  const r = state.retail;

  // ── 1. Stock : couverture faible (ruptures imminentes) ──
  if (r) {
    const stockTotal = Object.values(r.stock || {}).reduce((a, x) => a + (x.qty || 0), 0);
    const ventes7 = (r.ventes7 || []).reduce((a, v) => a + (v.unites || v.ventes || 0), 0);
    const parJour = ventes7 / 7;
    if (parJour >= 1) {
      const jours = Math.round(stockTotal / parJour);
      if (jours < 6) {
        insights.push(
          `**📦 Stock tendu** : ${jours} jour${jours > 1 ? 's' : ''} de couverture au rythme actuel (${Math.round(parJour)} articles/jour). ` +
          `Passez commande sans attendre — une rupture, c'est du CA perdu et des clients déçus.`,
        );
      }
    }

    // ── 2. Marge cumulée faible ──
    const caCumul = r.stats?.caCumul || 0;
    if (caCumul > 5000) {
      const margePct = Math.round(((r.stats?.margeCumul || 0) / caCumul) * 100);
      if (margePct < 32) {
        insights.push(
          `**📉 Marge sous pression** : ${margePct}% de marge cumulée. Vérifiez vos prix famille par famille ` +
          `(écran Assortiment & prix) et espacez les promotions.`,
        );
      }
    }

    // ── 3. Vitrine / marketing / fidélité ──
    const leviersActifs = Object.values(r.marketing?.leviers || {}).filter(Boolean).length;
    const sat = r.marketing?.satisfaction || 0;
    if (leviersActifs === 0 && (state.totalDays || 0) > 14) {
      insights.push(
        `**🪟 Vitrine éteinte** : aucun levier marketing actif. Une vitrine soignée et un peu de communication ` +
        `locale attirent du trafic pour quelques centaines d'euros par mois.`,
      );
    } else if (sat >= 65 && !r.marketing?.leviers?.fidelite) {
      insights.push(
        `**💳 Fidélisez !** Satisfaction à ${Math.round(sat)}/100 et pas de carte de fidélité : le moment idéal ` +
        `pour transformer vos clients contents en habitués.`,
      );
    }
  }

  // ── 4. Cash buildup with no investment ──
  // Heuristic: treasury > 12× average monthly net result AND > 100k€.
  const monthlyHistory = Array.isArray(state.monthlyHistory) ? state.monthlyHistory : [];
  if (monthlyHistory.length >= 3) {
    const recent = monthlyHistory.slice(-3);
    const avgMonthlyNet = recent.reduce((s, m) => s + (m.netResult || 0), 0) / recent.length;
    if (avgMonthlyNet > 5000 && (state.treasury || 0) > Math.max(100000, avgMonthlyNet * 12)) {
      insights.push(
        `**💰 Trésorerie excédentaire** : **${fmt(state.treasury)} €** en caisse alors que le résultat moyen tourne à ` +
        `${fmt(avgMonthlyNet)} €/mois. Le cash dort. Aménagement du magasin, second point de vente ou e-commerce ` +
        `pourraient le faire travailler.`,
      );
    }
  }

  if (insights.length === 0) return null;

  const intro = `Bonjour Patron,\n\nQuelques observations de fin de mois — rien d'urgent, juste des pistes :\n\n`;
  const outro = `\n\nÀ vous de voir si ça vaut la peine. Bonne fin de mois !\n\n— Nathalie`;
  const body = intro + insights.join('\n\n') + outro;
  const month = state.month || 1;
  const year = state.year || 1;

  return {
    senderType: 'support',
    senderName: 'Nathalie Robert (Assistante)',
    senderRole: 'role-nathalie-robert',
    tone: 'advice',
    kind: 'info',
    subject: `Pistes pour le mois prochain — bilan ${String(month).padStart(2, '0')}/${year}`,
    body,
    ttlDays: TTL_DAYS,
    receivedDay: state.totalDays || 0,
  };
}
